import React, { useCallback, useRef } from 'react'

interface RangeSliderProps {
  min: number
  max: number
  step?: number
  value: [number, number]
  onChange: (value: [number, number]) => void
  label?: string
  unit?: string
}

export default function RangeSlider({
  min,
  max,
  step = 1,
  value,
  onChange,
  label,
  unit,
}: RangeSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null)

  const pct = useCallback((v: number) => ((v - min) / (max - min)) * 100, [min, max])

  const handleThumbMouseDown = useCallback(
    (thumb: 'min' | 'max') => (e: React.MouseEvent) => {
      e.preventDefault()
      const track = trackRef.current
      if (!track) return

      const rect = track.getBoundingClientRect()

      const onMove = (ev: MouseEvent) => {
        const ratio = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width))
        const raw = min + ratio * (max - min)
        const snapped = Math.round(raw / step) * step

        if (thumb === 'min') {
          onChange([Math.min(snapped, value[1] - step), value[1]])
        } else {
          onChange([value[0], Math.max(snapped, value[0] + step)])
        }
      }

      const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }

      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [min, max, step, value, onChange]
  )

  const handleInputChange = useCallback(
    (thumb: 'min' | 'max') => (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value === '' ? undefined : Number(e.target.value)
      if (v === undefined) return
      if (thumb === 'min') {
        onChange([Math.max(min, Math.min(v, value[1] - step)), value[1]])
      } else {
        onChange([value[0], Math.min(max, Math.max(v, value[0] + step))])
      }
    },
    [min, max, step, value, onChange]
  )

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}{' '}
          <span className="text-gray-400 font-normal">
            {value[0]}
            {unit ? ` ${unit}` : ''} – {value[1]}
            {unit ? ` ${unit}` : ''}
          </span>
        </label>
      )}
      <div className="flex items-center gap-3">
        <input
          type="number"
          value={value[0]}
          onChange={handleInputChange('min')}
          className="w-20 px-2 py-1.5 text-sm text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          min={min}
          max={value[1] - step}
          step={step}
        />

        <div ref={trackRef} className="relative flex-1 h-2 bg-gray-200 rounded-full cursor-pointer">
          <div
            className="absolute h-full bg-blue-500 rounded-full"
            style={{
              left: `${pct(value[0])}%`,
              right: `${100 - pct(value[1])}%`,
            }}
          />

          <div
            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow cursor-grab active:cursor-grabbing"
            style={{ left: `calc(${pct(value[0])}% - 8px)` }}
            onMouseDown={handleThumbMouseDown('min')}
          />

          <div
            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow cursor-grab active:cursor-grabbing"
            style={{ left: `calc(${pct(value[1])}% - 8px)` }}
            onMouseDown={handleThumbMouseDown('max')}
          />
        </div>

        <input
          type="number"
          value={value[1]}
          onChange={handleInputChange('max')}
          className="w-20 px-2 py-1.5 text-sm text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          min={value[0] + step}
          max={max}
          step={step}
        />
      </div>
    </div>
  )
}
